// Empfänger der Einladung per E-Mail. Recruiter tippen Adressen oder fügen
// Listen aus Excel, Outlook oder Gmail ein. Getippt trennen Komma, Semikolon,
// Leerzeichen und Zeilenumbruch. Beim Einfügen bringt „Max Muster
// <max@firma.de>“ den Namen mit, ebenso eine Zeile mit genau einer Adresse und
// dem Namen daneben. Rein, ohne helpers/index.js. Ob eine Adresse gültig ist,
// prüft der Aufrufer mit EMAIL_REGEX.

// inviteTalent lädt ein Talent pro Aufruf. Mehr auf einmal wäre eine
// Massen-Aktion und gehört in einen Import.
export const MAX_RECIPIENTS = 50

// Trennt getippte Adressen
export const SEPARATOR = /[\s,;]+/

// <adresse> mit dem Namen davor, wie Outlook und Gmail kopieren
const ANGLE_ADDRESS = /<\s*([^<>\s]+@[^<>\s]+)\s*>/g

// Wörter einer Zeile, die als Name taugen. Großgeschrieben, damit Text wie
// „bitte einladen“ nicht zum Namen wird.
const NAME_WORD = /^\p{Lu}[\p{L}'’.-]*$/u
const MAX_NAME_WORDS = 4

// Kleingeschriebene Zusätze gehören zum Nachnamen (Ludwig van Beethoven)
const NAME_PARTICLES = new Set([
  'da',
  'de',
  'del',
  'der',
  'di',
  'du',
  'la',
  'le',
  'ten',
  'ter',
  'van',
  'vom',
  'von',
  'zu',
  'zum',
  'zur'
])

const recipientOf = (email, { firstname = '', lastname = '' } = {}) => ({
  email,
  firstname,
  lastname
})

/**
 * Adresse ohne das, was beim Kopieren drumherum hängen bleibt (mailto:,
 * Klammern, Anführungszeichen, Satzzeichen), klein geschrieben.
 *
 * @param {string} value
 * @returns {string}
 */
export const normalizeEmail = (value) =>
  String(value ?? '')
    .trim()
    .replace(/^["'(<[]+/, '')
    .replace(/^mailto:/i, '')
    .replace(/["')>\].,:;]+$/, '')
    .toLowerCase()

/**
 * Empfänger aus einer getippten Adresse, ohne Namen.
 *
 * @param {string} value
 * @returns {{ email: string, firstname: string, lastname: string }}
 */
export const typedRecipient = (value) => recipientOf(normalizeEmail(value))

/**
 * Zerlegt einen Anzeigenamen. „Muster, Max“ ist Nachname, Vorname. Sonst ist
 * das letzte Wort der Nachname, samt Zusätzen wie von oder van davor.
 *
 * @param {string} value
 * @returns {{ firstname: string, lastname: string }}
 */
export const parseDisplayName = (value) => {
  const name = String(value ?? '')
    .replace(/["“”„]/g, '')
    .replace(/^['’\s]+|['’\s]+$/g, '')
    .replace(/\s+/g, ' ')

  if (!name || name.includes('@')) return { firstname: '', lastname: '' }

  if (name.includes(',')) {
    const [lastname, ...rest] = name.split(',')
    return { firstname: rest.join(' ').trim(), lastname: lastname.trim() }
  }

  const words = name.split(' ')
  if (words.length === 1) return { firstname: name, lastname: '' }

  let start = words.length - 1
  while (start > 1 && NAME_PARTICLES.has(words[start - 1].toLowerCase())) {
    start -= 1
  }

  return {
    firstname: words.slice(0, start).join(' '),
    lastname: words.slice(start).join(' ')
  }
}

const isEmailToken = (token) => token.includes('@')

const tokensOf = (text) => text.split(SEPARATOR).filter(Boolean)

const bareRecipients = (text) =>
  tokensOf(text).filter(isEmailToken).map(typedRecipient)

// Eine Zeile ohne <…>. Mit genau einer Adresse sind die übrigen Wörter der
// Name (Excel-Zeile „Max Muster max@firma.de“), sonst gibt es keine Namen.
const lineRecipients = (line) => {
  const tokens = tokensOf(line)
  const emails = tokens.filter(isEmailToken)
  const words = tokens.filter((token) => !isEmailToken(token))

  const isName =
    emails.length === 1 &&
    words.length > 0 &&
    words.length <= MAX_NAME_WORDS &&
    words.every(
      (word) => NAME_WORD.test(word) || NAME_PARTICLES.has(word.toLowerCase())
    )

  if (!isName) return emails.map(typedRecipient)

  return [
    recipientOf(normalizeEmail(emails[0]), parseDisplayName(words.join(' ')))
  ]
}

// Eine Zeile mit <…>. Vor jeder Klammer steht der Name, davor können noch
// Adressen ohne Klammern stehen, etwa „a@firma.de, Max <max@firma.de>“.
const angleRecipients = (line, matches) => {
  const result = []
  let consumed = 0

  matches.forEach((match) => {
    const before = line.slice(consumed, match.index)
    consumed = match.index + match[0].length

    const [lastEmail] = [...before.matchAll(/[^\s,;]+/g)]
      .filter(([token]) => isEmailToken(token))
      .slice(-1)
    const nameStart = lastEmail ? lastEmail.index + lastEmail[0].length : 0

    result.push(...bareRecipients(before.slice(0, nameStart)))
    result.push(
      recipientOf(
        normalizeEmail(match[1]),
        parseDisplayName(before.slice(nameStart).replace(/^[\s,;]+/, ''))
      )
    )
  })

  return [...result, ...bareRecipients(line.slice(consumed))]
}

/**
 * Zerlegt eingefügten Text in Empfänger, in der Reihenfolge des Textes.
 * Wörter ohne @ fallen weg, wenn sie nicht als Name taugen. Doppelte bleiben
 * drin, das Zusammenfassen macht mergeRecipients.
 *
 * @param {string} text
 * @returns {{ email: string, firstname: string, lastname: string }[]}
 */
export const parseRecipients = (text) =>
  String(text ?? '')
    .split(/[\r\n;]+/)
    .flatMap((line) => {
      const matches = [...line.matchAll(ANGLE_ADDRESS)]
      return matches.length
        ? angleRecipients(line, matches)
        : lineRecipients(line)
    })
    .filter(({ email }) => email)

/**
 * Hängt Empfänger an, doppelte Adressen fasst sie zusammen. Kommt eine
 * Adresse ein zweites Mal mit Namen, ergänzt der Name den ersten Eintrag.
 *
 * @param {object[]} current
 * @param {object[]} incoming
 * @returns {object[]}
 */
export const mergeRecipients = (current, incoming) => {
  const merged = [...current]
  const indexByEmail = new Map(merged.map(({ email }, index) => [email, index]))

  incoming.forEach((recipient) => {
    if (!recipient.email) return

    const index = indexByEmail.get(recipient.email)
    if (index === undefined) {
      indexByEmail.set(recipient.email, merged.length)
      merged.push(recipientOf(recipient.email, recipient))
      return
    }

    const existing = merged[index]
    const hasName = existing.firstname || existing.lastname
    if (!hasName && (recipient.firstname || recipient.lastname)) {
      merged[index] = recipientOf(existing.email, recipient)
    }
  })

  return merged
}

/**
 * Alle Empfänger des Formulars, die Chips und die noch getippte Adresse.
 *
 * @param {{ recipients?: object[], email?: string }} values
 * @returns {object[]}
 */
export const allRecipients = ({ recipients = [], email = '' }) =>
  email.trim()
    ? mergeRecipients(recipients, [typedRecipient(email)])
    : recipients

const hasName = ({ firstname, lastname }) => !!(firstname || lastname)

/**
 * Namen wandern mit, wenn die Zahl der Adressen zwischen einer und mehreren
 * wechselt. Bei einer Adresse stehen sie in firstname und lastname des
 * Formulars, bei mehreren am Empfänger.
 *
 * @param {object} previous Formularwerte vorher
 * @param {object} next Formularwerte nachher
 * @returns {object} next, mit verschobenen Namen
 */
export const syncRecipientNames = (previous, next) => {
  const before = allRecipients(previous).length
  const after = allRecipients(next).length

  // Was im Einzelformular steht, gehört zur ersten Adresse
  if (before <= 1 && after > 1) {
    const [first, ...rest] = next.recipients
    if (!first || hasName(first) || !hasName(next)) return next

    return {
      ...next,
      recipients: [recipientOf(first.email, next), ...rest]
    }
  }

  if (after !== 1 || before === 1) return next

  const [only] = allRecipients(next)
  const chip = next.recipients.find(({ email }) => email === only.email)

  // Zurück bei einer Adresse gelten deren Namen, nicht die einer entfernten
  if (before > 1) {
    return {
      ...next,
      firstname: chip?.firstname || '',
      lastname: chip?.lastname || ''
    }
  }

  // Eingefügt mit Namen, die Felder sind noch leer
  if (chip && hasName(chip) && !hasName(next)) {
    return { ...next, firstname: chip.firstname, lastname: chip.lastname }
  }

  return next
}
